import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { parseError } from 'hooks/shared/error';
import { ContactMethod } from 'components/Notifications/recipientTypes';
import apiConfig from 'constants/apiConfig';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectGroupGuid, selectIncidentGuid } from 'slices/commonSelectors';

export const filterOptions = ['active', 'historical', 'sent'];

export type NotificationDto = {
  id: string;
  groupGuid: string;
  incidentId: string | null;
  acknowledgementRequired: boolean;
  version: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string | null;
  sentAt: string | null;
  autoArchiveDate: string | null;
  priority: NotificationPriority;
  status: NotificationStatus;
  sourceItemId: string | null;
  scheduledDate: string | null;
  aors: string[];
};

export type NotificationMetadataDto = {
  id: string;
  notificationId: string;
  emailSubject: string | null;
  emailMessage: string | null;
  smsMessage: string | null;
  teamsMessage: string | null;
  phoneMessage: string | null;
  appMessage: string | null;
  createdAt: string;
  updatedAt: string;
  users: string[];
  teams: string[];
  sendEmail: boolean;
  sendSms: boolean;
  sendTeams: boolean;
  sendPhone: boolean;
  sendApp: boolean;
};

export type NotificationWithMetadataDto = NotificationDto & {
  metadata: NotificationMetadataDto;
  hasUnarchivedRecipients?: boolean;
};

export enum NotificationStatus {
  CREATED = 'created', // status for tracking. notification has been created
  DRAFT = 'draft', // user saved notification as a draft
  SCHEDULED = 'scheduled', // notification is waiting in BullMQ
  PROCESSING = 'processing', // status is for tracking. notification is being processed by worker
  CANCELLED = 'cancelled', // user cancelled before sending
  SENT = 'sent', // successfully sent to all recipients
  FAILED = 'failed', // failed to send to any recipient
  PARTIAL = 'partial', // Some recipients succeeded, some failed
}

export enum NotificationType {
  EMAIL = 'email',
  SMS = 'sms',
  IN_APP = 'inApp',
  TEAMS = 'teams',
  TTS_HOME = 'ttsHome',
  TTS_CELL = 'ttsCell',
}

export enum NotificationPriority {
  INFO = 'info',
  ADVISORY = 'advisory',
  WATCH = 'watch',
  WARNING = 'warning',
  CRITICAL = 'critical', // For "Stand Down Safety" alerts
}

// Currently in use by the legacy notification alert system
export type SendNotificationFormFields = {
  subject: string;
  contact_method: ContactMethod[];
  unified_message_choice: boolean;
  email_message?: string;
  sms_message?: string;
  unified_message?: string;
  individuals?: UUID[];
  teams?: UUID[];
  sendType: string;
  scheduledDate?: Date | null;
};

export type CreateNotificationFormFields = {
  incidentId: string | null;
  acknowledgementRequired: boolean;
  autoArchiveDate: Date | null;
  priority: NotificationPriority;
  emailSubject: string | null;
  emailMessage: string | null;
  smsMessage: string | null;
  teamsMessage: string | null;
  phoneMessage: string | null;
  appMessage: string | null;
  userIds: string[];
  teamIds: string[];
  sourceItemId: string | null;
  sourceItemType: string | null;
  scheduledDate: Date | null;
  notificationTypes: NotificationType[];
  aors?: string[];
};

export const useCreateNotification = () => {
  const groupGuid = useSelector(selectGroupGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateNotificationFormFields) => {
      await axios(apiConfig.apiGateway.notifications, {
        method: 'POST',
        data: JSON.stringify({
          ...data,
          groupGuid: groupGuid,
        }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['userNotifications', groupGuid],
      });
      if (groupGuid) {
        queryClient.invalidateQueries({
          queryKey: ['notificationsByGroup', groupGuid],
        });
      }
    },
    onError: (error) => {
      toast.error(parseError(error));
    },
  });
};

export const useSendNotification = () => {
  const groupGuid = useSelector(selectGroupGuid);
  const incidentGuid = useSelector(selectIncidentGuid);
  return useMutation({
    mutationFn: async (data: SendNotificationFormFields) => {
      return await axios(apiConfig.apiGateway.sendPratusAlert, {
        method: 'POST',
        data: JSON.stringify({
          ...data,
          group_guid: groupGuid,
          incident_guid: incidentGuid,
        }),
      });
    },
    onError: (error) => {
      toast.error(parseError(error));
    },
  });
};

export const useNotificationsByGroup = () => {
  const groupGuid = useSelector(selectGroupGuid);
  return useQuery<NotificationWithMetadataDto[]>({
    queryKey: ['notificationsByGroup', groupGuid],
    enabled: !!groupGuid,
    queryFn: async () => {
      const response = await axios.get(apiConfig.apiGateway.notifications, {
        params: { groupId: groupGuid },
      });
      return response.data;
    },
  });
};

export const useArchiveNotificationForAll = () => {
  const groupGuid = useSelector(selectGroupGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { notificationId: string }) => {
      return await axios.post(
        `${apiConfig.apiGateway.notifications}/${data.notificationId}/archive-all`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['userNotifications', groupGuid],
      });
      queryClient.invalidateQueries({
        queryKey: ['notificationsByGroup', groupGuid],
      });
    },
    onError: (error) => {
      toast.error(parseError(error));
    },
  });
};
